// src/pages/DoctorList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import Select from "react-select";
import { HospitalServices } from "../../../services/HospitalServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import EditDoctorModal from "./EditDoctorModal";
import CustomPagination from "../../../utils/paginationUtils";
import EditIcon from "../../../assets/editIcon.svg";

const DoctorList = () => {
  const [hospitalList, setHospitalList] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState(null);

  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const [editOpen, setEditOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);

  const loadHospitals = async () => {
    try {
      const res = await HospitalServices.gethospitals("", 1, 1500);
      const list = res?.data?.list ?? [];
      setHospitalList(list);
      if (list.length) setSelectedHospital(null);
    } catch (err) {
      handleApiErrorResponses(err);
    }
  };

  const hospitalId = useMemo(
    () => selectedHospital?.id ?? "",
    [selectedHospital]
  );

  // Load doctors for the page; always replace doctors array per page
  const loadDoctors = async () => {
    setLoading(true);
    try {
      const res = await HospitalServices.getAllDoctorsList(
        hospitalId,
        page,
        ""
      );
      const list = res?.data?.list ?? [];
      setHasMore(Boolean(res?.data?.hasMore));
      setDoctors(list);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (err) {
      handleApiErrorResponses(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadHospitals();
  }, []);

  // Reset page to 1 when hospital filter changes
  useEffect(() => {
    setPage(1);
  }, [hospitalId]);

  // Load doctors whenever hospital or page changes
  useEffect(() => {
    loadDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalId, page]);

  const onOpenEdit = (doctor) => {
    setEditingDoctor(doctor);
    setEditOpen(true);
  };

  const onCloseEdit = (updated) => {
    setEditOpen(false);
    setEditingDoctor(null);
    if (updated) {
      loadDoctors();
    }
  };

  return (
    <div className="p-[32px] mt-[92px] w-full">
      <span className="path_line">
        <WidgetsOutlinedIcon style={{ height: 24 }} /> &nbsp; &nbsp;/ &nbsp;
        &nbsp; Profile &nbsp; &nbsp;/ &nbsp; &nbsp; Doctors
      </span>

      <div className="flex items-center justify-between mt-2">
        <div className="text-2xl font-semibold">Doctor List</div>
      </div>

      <hr className="w-full border border-[#E4E4E4]" />

      {/* Hospital filter */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Select
            options={hospitalList}
            getOptionLabel={(o) => o?.name}
            getOptionValue={(o) => o?.id}
            value={selectedHospital}
            onChange={setSelectedHospital}
            isClearable={true}
            placeholder="Select Hospital"
          />
        </div>
      </div>

      {/* table */}
      <div className="mt-6">
        {loading ? (
          <div className="flex justify-center p-10">
            <CircularProgress />
          </div>
        ) : doctors.length === 0 ? (
          <div className="text-center text-[#191919] py-8">
            No doctors found
          </div>
        ) : (
          <div className="w-full overflow-auto rounded-[12px] border border-[#e4e4e4]">
            <table className="min-w-[800px] w-full">
              <thead className="bg-[#FAFAFA] text-left">
                <tr className="text-[#191919]">
                  <th className="px-4 py-3 w-[40%]">Full Name</th>
                  <th className="px-4 py-3 w-[25%]">Doctor Type</th>
                  <th className="px-4 py-3 w-[25%]">Highest Qualification</th>
                  <th className="px-4 py-3 w-[10%] text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {doctors.map((d) => (
                  <tr key={d.id} className="border-t border-[#F0F0F0]">
                    <td className="px-4 py-3">{d.fullName || "-"}</td>
                    <td className="px-4 py-3">{d.doctorType || "-"}</td>
                    <td className="px-4 py-3">
                      {d.highestQualification || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center">
                        <img
                          src={EditIcon}
                          alt="edit-icon"
                          className="h-[30px] w-[30px] cursor-pointer"
                          onClick={() => onOpenEdit(d)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex justify-center mt-8 my-8">
              {/* Pass totalPages here for correct navigation */}
              <CustomPagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
              />
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editOpen && (
        <EditDoctorModal
          open={editOpen}
          onClose={onCloseEdit}
          doctor={editingDoctor}
        />
      )}
    </div>
  );
};

export default DoctorList;
